
interface ActivityFieldConfig {
  mandatory: string[];
  optional: string[];
}

export const LOCATION_FIELD_TYPES = {
  flightFrom: 'airport',
  flightTo: 'airport',

  country_from: 'country',
  country_to: 'country',

  airline: 'airline',

  address: 'address',
  pickupAddress: 'address',
  dropOffAddress: 'address',
  officeAddress: 'address',
  departureAddress: 'address',
  arrivalAddress: 'address',
  startingPortAddress: 'address',
  endingPortAddress: 'address',
} as const;

export const UI_TO_API_FIELD_MAP: Record<string, string> = {
  vehicleType: 'vehicle_type',
  vehicleDescription: 'vehicle_description',
  shipName: 'transport_name',
  carrierName: 'carrier_name',

  departureLocation: 'location_from_name',
  arrivalLocation: 'location_to_name',
  startingPortName: 'location_from_name',
  endingPortName: 'location_to_name',
  departureStation: 'location_from_name',
  arrivalStation: 'location_to_name',

  departureDate: 'departure_date_time',
  departureTime: 'departure_date_time',
  arrivalDate: 'arrival_date_time',
  arrivalTime: 'arrival_date_time',
  startDate: 'start_date_time',
  startTime: 'start_date_time',
  endDate: 'end_date_time',
  endTime: 'end_date_time',
  checkInDate: 'check_in_date_time',
  checkInTime: 'check_in_date_time',
  checkOutDate: 'check_out_date_time',
  checkOutTime: 'check_out_date_time',
  date: 'start_date_time',
  time: 'start_date_time',

  flightFrom: 'flight_from',
  flightTo: 'flight_to',
  flightNumber: 'flight_number',
  airline: 'airline_iata_code',

  seatNumber: 'seat',
  class: 'seat_type',
  trainNumber: 'train',
  trainType: 'train_type',
  cabinNumber: 'cabin',
  cabinType: 'cabin_type',
  bookingNumber: 'booking',
  ticketBookingNumber: 'booking',
  amountSpent: 'amount_spend',
  numberOfPassengers: 'number_of_passengers',
  borderCheckpoint: 'border_checkpoint',
  phoneNumber: 'phone_number',

  pickupAddress: 'pick_up_address',
  dropOffAddress: 'drop_off_address',
  officeAddress: 'address',
  departureAddress: 'departure_address',
  arrivalAddress: 'arrival_address',
  startingPortAddress: 'starting_port_address',
  endingPortAddress: 'ending_port_address',
};

export const ACTIVITY_FIELD_CONFIG: Record<string, ActivityFieldConfig> = {
  Flight: {
    mandatory: [
      'activityType',
      'flightFrom',
      'flightTo',
      'departureDate',
      'departureTime',
      'arrivalDate',
      'arrivalTime',
    ],
    optional: ['airline', 'flightNumber', 'ticketBookingNumber', 'seatNumber', 'notes', 'amountSpent'],
  },

  Hotel: {
    mandatory: ['activityType', 'name', 'checkInDate', 'checkInTime', 'checkOutDate', 'checkOutTime'],
    optional: ['address', 'bookingNumber', 'phone', 'email', 'link', 'notes', 'amountSpent'],
  },

  Activity: {
    mandatory: ['activityType', 'name', 'startDate', 'startTime', 'endDate', 'endTime'],
    optional: ['place', 'address', 'bookingNumber', 'email', 'phone', 'link', 'notes', 'amountSpent'],
  },

  Concert: {
    mandatory: ['activityType', 'name', 'startDate', 'startTime', 'endDate', 'endTime'],
    optional: ['place', 'address', 'bookingNumber', 'seatNumber', 'email', 'phone', 'link', 'notes', 'amountSpent'],
  },

  Theatre: {
    mandatory: ['activityType', 'name', 'startDate', 'startTime', 'endDate', 'endTime'],
    optional: ['place', 'address', 'bookingNumber', 'seatNumber', 'email', 'phone', 'link', 'notes', 'amountSpent'],
  },

  Meeting: {
    mandatory: ['activityType', 'name', 'startDate', 'startTime', 'endDate', 'endTime'],
    optional: ['place', 'address', 'bookingNumber', 'email', 'phone', 'link', 'notes', 'amountSpent'],
  },

  Shopping: {
    mandatory: ['activityType', 'startDate', 'startTime', 'endDate', 'endTime'],
    optional: ['name', 'place', 'address', 'email', 'phone', 'link', 'notes', 'amountSpent'],
  },

  Restaurant: {
    mandatory: ['activityType', 'name', 'startDate', 'startTime', 'endDate', 'endTime'],
    optional: ['place', 'address', 'bookingNumber', 'email', 'phone', 'link', 'notes', 'amountSpent'],
  },

  Tour: {
    mandatory: ['activityType', 'name', 'startDate', 'startTime', 'endDate', 'endTime'],
    optional: ['place', 'address', 'bookingNumber', 'email', 'phone', 'link', 'notes', 'amountSpent'],
  },

  CarRental: {
    mandatory: ['activityType', 'startDate', 'startTime', 'endDate', 'endTime'],
    optional: [
      'name',
      'pickupAddress',
      'sameAsPickupAddress',
      'dropOffAddress',
      'officeAddress',
      'vehicleType',
      'bookingNumber',
      'email',
      'phone',
      'link',
      'notes',
      'amountSpent',
    ],
  },

  Parking: {
    mandatory: ['activityType', 'place', 'startDate', 'startTime', 'endDate', 'endTime'],
    optional: ['address', 'bookingNumber', 'parkingSpaceNumber', 'phone', 'email', 'link', 'notes', 'amountSpent'],
  },

  Directions: {
    mandatory: ['activityType', 'place', 'startDate', 'startTime', 'endDate', 'endTime'],
    optional: ['address', 'bookingNumber', 'phone', 'email', 'link', 'notes', 'amountSpent'],
  },

  BorderCrossing: {
    mandatory: ['activityType', 'date', 'time', 'country_from', 'country_to'],
    optional: ['borderCheckpoint', 'address', 'email', 'phone', 'link', 'notes', 'amountSpent'],
  },

  Ferry: {
    mandatory: ['activityType', 'name', 'departureDate', 'departureTime', 'arrivalDate', 'arrivalTime'],
    optional: [
      'carrierName',
      'bookingNumber',
      'departureLocation',
      'departureAddress',
      'arrivalLocation',
      'arrivalAddress',
      'vehicleDescription',
      'numberOfPassengers',
      'seatNumber',
      'email',
      'phone',
      'link',
      'notes',
      'amountSpent',
    ],
  },

  Cruise: {
    mandatory: ['activityType', 'name', 'departureDate', 'departureTime', 'arrivalDate', 'arrivalTime'],
    optional: [
      'shipName',
      'bookingNumber',
      'startingPortName',
      'startingPortAddress',
      'sameAsStartingPort',
      'endingPortName',
      'endingPortAddress',
      'cabinType',
      'cabinNumber',
      'email',
      'phone',
      'link',
      'notes',
      'amountSpent',
    ],
  },

  Rail: {
    mandatory: ['activityType', 'name', 'departureDate', 'departureTime', 'arrivalDate', 'arrivalTime'],
    optional: [
      'bookingNumber',
      'departureStation',
      'departureAddress',
      'arrivalStation',
      'arrivalAddress',
      'trainType',
      'trainNumber',
      'class',
      'seatNumber',
      'email',
      'phone',
      'link',
      'notes',
      'amountSpent',
    ],
  },

  Transportation: {
    mandatory: ['activityType', 'vehicleType', 'departureDate', 'departureTime', 'arrivalDate', 'arrivalTime'],
    optional: [
      'carrierName',
      'bookingNumber',
      'departureLocation',
      'departureAddress',
      'arrivalLocation',
      'arrivalAddress',
      'numberOfPassengers',
      'email',
      'phone',
      'link',
      'notes',
      'amountSpent',
    ],
  },
};

export const getFieldsForActivity = (activityType: string) => {
  return ACTIVITY_FIELD_CONFIG[activityType] || { mandatory: [], optional: [] };
};

export const shouldShowField = (fieldName: string, activityType: string) => {
  if (!activityType) return fieldName === 'activityType';

  const config = ACTIVITY_FIELD_CONFIG[activityType];
  if (!config) return false;

  return config.mandatory.includes(fieldName) || config.optional.includes(fieldName);
};

export const getFieldInputType = (fieldName: string): 'airport' | 'country' | 'address' | 'airline' | 'default' => {
  return LOCATION_FIELD_TYPES[fieldName as keyof typeof LOCATION_FIELD_TYPES] || 'default';
};

export const getApiFieldName = (uiFieldName: string): string => {
  return UI_TO_API_FIELD_MAP[uiFieldName] || uiFieldName;
};

const API_TO_UI_FIELD_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(UI_TO_API_FIELD_MAP).map(([uiField, apiField]) => [apiField, uiField])
);

export const getActivityUiFieldFromApiAttr = (apiAttr: string): string | undefined => {
  if (API_TO_UI_FIELD_MAP[apiAttr]) {
    return API_TO_UI_FIELD_MAP[apiAttr];
  }
  if (apiAttr === 'activity_type') {
    return 'activityType';
  }
  return undefined;
};
